import { useQuery } from '@apollo/client';
import { SelectionDrawer } from '@openx/components-core/lib/SelectionDrawer/SelectionDrawer.tsx';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ListCountriesDocument,
  type ListCountriesQuery,
} from '../../../../../../../graphql/fragment/audiences/ListCountries.generated.tsx';
import {
  ListRegionsDocument,
  type ListRegionsQuery,
} from '../../../../../../../graphql/fragment/audiences/ListRegions.generated.tsx';
import { useProviders } from '../../../../../../../modules/audiences/hooks/useProviders.ts';
import { AudienceFields, type AudienceForm } from '../../../../types.ts';
import { type DrawerState, type DrawerTypeValue, drawerType } from '../../drawerTypes.ts';
import { useReferenceData } from '../../hooks/useReferenceData.ts';
import { selectionDrawerStyles } from './components/constants.ts';
import { SearchAndFilters } from './components/SearchAndFilters.tsx';
import { LookalikeSelectionModal } from './components/SegmentsList/lookalike/LookalikeSelectionModal.tsx';
import { SegmentListItem } from './components/SegmentsList/SegmentListItem.tsx';
import { SelectedSegmentItemAdapter } from './components/SelectedSegmentItemAdapter.tsx';
import { useAudienceDrawerAdapter } from './hooks/useAudienceDrawerAdapter.ts';
import { useGetDrawerConfig } from './hooks/useDrawerConfig.ts';
import { useEnforceMarketUnionAfterApply } from './hooks/useEnforceMarketUnionAfterApply.ts';
import { useGetSegmentsValues } from './hooks/useGetSegmentsValues.ts';
import { useHasOxpsSegments } from './hooks/useHasOxpsSegments.ts';
import { useMarketSelection } from './hooks/useMarketSelection.ts';
import { useSegmentFilters } from './hooks/useSegmentFilters.ts';
import { isFormSegmentArray } from './utils.ts';

type SegmentsSelectionDrawerProps = {
  drawerState: Extract<DrawerState, { isOpen: true }>;
  setDrawerState: Dispatch<SetStateAction<DrawerState>>;
  currentDrawerType: DrawerTypeValue;
  isCreate?: boolean;
  isCreateFromTemplate?: boolean;
  onMarketUnionCoerced?: () => void;
};

export const SegmentsSelectionDrawer = ({
  drawerState,
  setDrawerState,
  currentDrawerType,
  isCreate,
  isCreateFromTemplate,
  onMarketUnionCoerced,
}: SegmentsSelectionDrawerProps) => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext<AudienceForm>();
  const observableAccountId = useWatch({ name: AudienceFields.OrganizationId, control });
  const observableDirectProvider = useWatch({ name: AudienceFields.DirectAudienceProvider, control });

  const { allowedProviders } = useProviders(observableAccountId);
  const segmentsGroup = getValues(drawerState.fieldPath);
  const { hasMarketSelection, isNonUSMarket, marketContext } = useMarketSelection({
    marketContext: drawerState.marketContext,
    segmentsGroup,
  });
  const { providers: providersMap, organizations: organizationsMap } = useReferenceData({ marketContext });

  const { useSegmentSelection, useSegmentDataFetch, useSegmentPagination, useDrawerActions, useLookalikeModal } =
    useGetDrawerConfig(currentDrawerType);

  const enforceMarketUnionAfterApply = useEnforceMarketUnionAfterApply({ isCreate, isCreateFromTemplate });
  const enforceMarketUnionAfterApplyWithNotice = useCallback(
    (fieldPath: string): boolean => {
      const wasMarketUnionCoerced = enforceMarketUnionAfterApply(fieldPath);

      if (wasMarketUnionCoerced) {
        onMarketUnionCoerced?.();
      }

      return wasMarketUnionCoerced;
    },
    [enforceMarketUnionAfterApply, onMarketUnionCoerced]
  );

  const { data: regionsData } = useQuery<ListRegionsQuery>(ListRegionsDocument);
  const { data: countriesData } = useQuery<ListCountriesQuery>(ListCountriesDocument);
  const regions = regionsData?.region ?? [];
  const countries = countriesData?.country ?? [];

  const { searchPhrase, setSearchPhrase, filters, onFiltersChange, dataSourceFilter, setDataSourceFilter } =
    useSegmentFilters({
      hideDataTypeFilter: isNonUSMarket,
      hideDataProviderFilter: hasMarketSelection,
      hideReachFilter: isNonUSMarket,
    });

  const formStateSegments = useGetSegmentsValues({ segmentsGroup });

  const {
    segments: fetchedSegments,
    loading: loadingSegments,
    totalCount,
  } = useSegmentDataFetch({
    accountId: observableAccountId,
    dataSourceFilters: dataSourceFilter,
    filters,
    marketContext,
    searchPhrase: searchPhrase,
    linkedProviders: allowedProviders,
    providerId: observableDirectProvider || '',
  });

  // todo: to be removed in future - waiting for OXPS segments to be loaded in prod
  // https://openxtechinc.atlassian.net/browse/RS-538
  const hasOxpsSegments = useHasOxpsSegments();

  const paginationResult = useSegmentPagination({
    accountId: observableAccountId,
    dataSourceFilters: dataSourceFilter,
    filters: filters,
    marketContext,
    searchPhrase: searchPhrase,
    linkedProviders: allowedProviders,
    segments: isFormSegmentArray(fetchedSegments) ? fetchedSegments : undefined,
    definitions: !isFormSegmentArray(fetchedSegments) ? fetchedSegments : undefined,
    providerId: observableDirectProvider || '',
  });

  const segmentSelectionProps = useSegmentSelection(formStateSegments);

  const lookalikeModal = useLookalikeModal({
    segmentSelectionProps: segmentSelectionProps,
    fetchedSegments: !isFormSegmentArray(fetchedSegments) ? fetchedSegments : undefined,
  });

  // Adapter: Convert domain hooks to SelectionDrawer API
  const drawerAdapter = useAudienceDrawerAdapter({
    currentDrawerType,
    drawerState,
    setDrawerState,
    segmentsGroup,
    segmentSelectionProps,
    enforceMarketUnionAfterApply: enforceMarketUnionAfterApplyWithNotice,
    totalCount,
    loadingSegments,
    paginationResult,
    lookalikeModal,
    useDrawerActions,
  });

  const drawerTitle = currentDrawerType === drawerType.LOOKALIKE ? t('Lookalike Segments') : t('Segments');

  const existingSegments =
    lookalikeModal.selectedDefinitionForModal && 'segmentsByDefinition' in segmentSelectionProps
      ? segmentSelectionProps.segmentsByDefinition[lookalikeModal.selectedDefinitionForModal.id]
      : undefined;

  // Search and filters slot
  const searchAndFiltersSlot = useMemo(
    () => (
      <SearchAndFilters
        searchPhrase={searchPhrase}
        setSearchPhrase={setSearchPhrase}
        filters={filters}
        onFiltersChange={onFiltersChange}
        providers={providersMap}
        organizations={organizationsMap}
        hasLinkedProviders={!!allowedProviders.length}
        hasOxpsSegments={hasOxpsSegments}
        isLookalike={currentDrawerType === drawerType.LOOKALIKE}
        dataSourceFilter={dataSourceFilter}
        setDataSourceFilter={setDataSourceFilter}
        hideDataTypeFilter={isNonUSMarket}
        hideDataProviderFilter={hasMarketSelection}
        hideReachFilter={isNonUSMarket}
      />
    ),
    [
      searchPhrase,
      setSearchPhrase,
      filters,
      onFiltersChange,
      providersMap,
      organizationsMap,
      allowedProviders.length,
      hasOxpsSegments,
      currentDrawerType,
      dataSourceFilter,
      setDataSourceFilter,
      isNonUSMarket,
      hasMarketSelection,
    ]
  );

  return (
    <>
      <SelectionDrawer
        dataTest="audience-segment-drawer"
        title={drawerTitle}
        open={drawerState.isOpen}
        leftPanel={drawerAdapter.leftPanel}
        rightPanel={drawerAdapter.rightPanel}
        actions={drawerAdapter.actions}
        discardConfig={drawerAdapter.discardConfig}
        slots={{
          leftPanelHeader: searchAndFiltersSlot,
        }}
        drawerSx={selectionDrawerStyles}
        renderItem={props => (
          <SegmentListItem
            type={currentDrawerType}
            segment={props.item}
            isSelected={props.isSelected}
            onSelect={() => props.onSelect(props.item)}
            searchPhrase={searchPhrase}
            providerName={props.item.provider_id ? providersMap[props.item.provider_id] : ''}
            organizationName={props.item.account_id ? organizationsMap[props.item.account_id] : ''}
            selectedAccountId={observableAccountId}
            regions={regions}
            countries={countries}
          />
        )}
        renderSelectedItem={props => (
          <SelectedSegmentItemAdapter
            {...props}
            onEditLookalike={lookalikeModal?.handleEditLookalike}
            regions={regions}
            countries={countries}
          />
        )}
      />

      {currentDrawerType === drawerType.LOOKALIKE && lookalikeModal.selectedDefinitionForModal && (
        <LookalikeSelectionModal
          open={lookalikeModal.showLookalikeModal}
          onClose={lookalikeModal.handleModalClose}
          onConfirm={lookalikeModal.handleModalConfirm}
          definition={lookalikeModal.selectedDefinitionForModal}
          isEditMode={lookalikeModal.isEditMode}
          existingSegments={existingSegments}
        />
      )}
    </>
  );
};
